/**
 * WCAG-compliant contrast colour utilities.
 *
 * Design decision: The booking and heatmap colour palette uses light pastel
 * tones throughout (e.g. #9FE1CB, #CECBF6, #bfdbfe). These all achieve
 * far better contrast with dark text than with white text. To keep the UI
 * consistent — no jarring mix of black and white labels — we always render
 * text as dark (#000000 at reduced opacity via the consuming component).
 *
 * Utility functions are retained for future use (e.g. dark-mode support or
 * custom brand colours that fall outside the pastel range).
 */

/**
 * Parse a hex colour string (#rrggbb or #rgb) into RGB components (0–255).
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '');
  if (cleaned.length === 3) {
    const r = parseInt(cleaned[0] + cleaned[0], 16);
    const g = parseInt(cleaned[1] + cleaned[1], 16);
    const b = parseInt(cleaned[2] + cleaned[2], 16);
    return { r, g, b };
  }
  if (cleaned.length === 6) {
    const r = parseInt(cleaned.substring(0, 2), 16);
    const g = parseInt(cleaned.substring(2, 4), 16);
    const b = parseInt(cleaned.substring(4, 6), 16);
    return { r, g, b };
  }
  return null;
}

/**
 * Convert a hex colour to an `rgba()` string with the given alpha.
 * Falls back to `transparent` when the hex cannot be parsed.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return 'transparent';
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * Convert a single 0–255 channel value to its linearised sRGB value,
 * as specified in WCAG relative luminance calculation.
 */
function linearise(channel: number): number {
  const sRGB = channel / 255;
  return sRGB <= 0.04045 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
}

/**
 * Calculate the relative luminance of a colour (WCAG definition).
 * Returns a value in the range [0, 1].
 */
export function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const R = linearise(rgb.r);
  const G = linearise(rgb.g);
  const B = linearise(rgb.b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/**
 * Calculate the WCAG contrast ratio between two colours.
 * Returns a value in the range [1, 21].
 */
export function getContrastRatio(hex1: string, hex2: string): number {
  const l1 = getRelativeLuminance(hex1);
  const l2 = getRelativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function toHex(rgb: { r: number; g: number; b: number }): string {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, '0');
  return `#${channel(rgb.r)}${channel(rgb.g)}${channel(rgb.b)}`;
}

/**
 * Blend two colours together. `amount` is the weight of `target`, 0–1.
 */
export function mixColors(hex: string, target: string, amount: number): string {
  const from = hexToRgb(hex);
  const to = hexToRgb(target);
  if (!from || !to) return hex;
  const t = Math.min(1, Math.max(0, amount));
  return toHex({
    r: from.r + (to.r - from.r) * t,
    g: from.g + (to.g - from.g) * t,
    b: from.b + (to.b - from.b) * t,
  });
}

/** Surfaces a booking tile can sit on, used for outline contrast checks. */
const LIGHT_SURFACE = '#ffffff';
const DARK_SURFACE = '#111827';
/** WCAG 2.1 non-text contrast minimum (1.4.11). */
const MIN_NON_TEXT_CONTRAST = 3;

/**
 * Nudge a colour towards mid-tone until it is visible as a border/outline on
 * both light and dark surfaces.
 *
 * Very light colours are darkened (they vanish against a white row); very dark
 * colours are lightened (they vanish against a dark-mode row). Colours that
 * already meet the 3:1 non-text contrast requirement are returned unchanged, so
 * a user's chosen status colour is only altered when it genuinely isn't visible.
 */
export function getAccessibleOutlineColor(hex: string): string {
  if (!hexToRgb(hex)) return hex;
  let color = hex;

  // Too light for a white surface → darken.
  for (let i = 0; i < 20; i++) {
    if (getContrastRatio(color, LIGHT_SURFACE) >= MIN_NON_TEXT_CONTRAST) break;
    color = mixColors(color, '#000000', 0.1);
  }

  // Too dark for a dark surface → lighten, but never at the cost of the
  // light-surface contrast gained above.
  for (let i = 0; i < 20; i++) {
    if (getContrastRatio(color, DARK_SURFACE) >= MIN_NON_TEXT_CONTRAST) break;
    const lighter = mixColors(color, '#ffffff', 0.1);
    if (getContrastRatio(lighter, LIGHT_SURFACE) < MIN_NON_TEXT_CONTRAST) break;
    color = lighter;
  }

  return color;
}

/**
 * Always returns dark text colour for use over booking / heatmap backgrounds.
 *
 * The entire booking palette uses light pastel tones — always returning dark
 * text gives consistent, WCAG-compliant contrast without the visual
 * inconsistency of mixing black and white labels across adjacent cells.
 */
export function getContrastTextColor(_backgroundHex: string): '#000000' {
  return '#000000';
}
